import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  FilterRowSelection,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import {
  getLabelFromOptionValues,
  Option,
} from '@/components/Filter/FilterRow/FilterRow.tsx';
import { useServices } from '@/components/ServiceContext';
import log from 'loglevel';

export interface Props {
  isLoading?: boolean;
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowValue: FilterRowSelection) => void;
  options: Option[];
  onFocusChange?: () => void;
  filterDefinition: FilterDefinition;
  autoFocus?: boolean;
}

const FilterRowMultiSelect: React.FC<Props> = ({
  isLoading = false,
  filterRowSelection,
  onChange,
  options = [],
  onFocusChange,
  filterDefinition,
  autoFocus = false,
}) => {
  const { label, labelMapping, isDatabaseField } = filterDefinition;
  const { flagDefinitionService } = useServices();

  // State for flag mapping
  const [flagMapping, setFlagMapping] = useState<
    Record<string, { friendlyName: string; description?: string }>
  >({});

  // Load flag definitions for array fields
  useEffect(() => {
    const loadFlagMapping = async () => {
      if (
        filterDefinition.isArrayField &&
        filterDefinition.databaseMetadata?.columnName
      ) {
        try {
          // Get flag definitions and build mapping locally
          const flagDefinitions =
            await flagDefinitionService.getFlagDefinitionsByField(
              'houselist', // TODO: Make this dynamic based on the list type
              filterDefinition.databaseMetadata.columnName
            );

          // Build the mapping from flag definitions
          const mapping: Record<
            string,
            { friendlyName: string; description?: string }
          > = {};
          flagDefinitions.forEach(flag => {
            mapping[flag.flagValue] = {
              friendlyName: flag.friendlyName,
              description: flag.description || undefined,
            };
          });

          setFlagMapping(mapping);
        } catch (error) {
          log.warn('Failed to load flag mapping:', error);
        }
      }
    };

    loadFlagMapping();
  }, [
    filterDefinition.isArrayField,
    filterDefinition.databaseMetadata?.columnName,
    flagDefinitionService,
  ]);

  // Normalize null/undefined to '' for comparison
  const normalize = (v: FilterRowSelection['value'][0]) =>
    v == null ? '' : String(v);

  // Guard: never allow a literal "ALL" from upstream options to enter our lists
  const safeOptions = options.filter(opt => normalize(opt.value) !== 'ALL');

  const normalizedSelected = filterRowSelection.value.map(normalize);
  const allOptionValues = safeOptions.map(opt => normalize(opt.value));

  // "ALL" toggle state. We don't want to set this true on mount even if
  // normalizedSelected already covers everything, so we skip once.
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);

  // One-time hydration: if defaultValue contains "ALL", expand to every option and notify parent.
  const didHydrateAllRef = useRef(false);
  useLayoutEffect(() => {
    const wantsAll = normalizedSelected.includes('ALL');
    if (wantsAll && !didHydrateAllRef.current && allOptionValues.length > 0) {
      didHydrateAllRef.current = true;
      setShowAll(true);
      onChange({
        ...filterRowSelection,
        value: allOptionValues.map(v => (v === '' ? null : v)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSelected, allOptionValues.length]);

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const vals = Array.isArray(e.target.value) ? e.target.value : [];
    if (vals.includes('ALL')) {
      setShowAll(prev => {
        const newShowAll = !prev;
        if (newShowAll) {
          // When selecting ALL, set value to all available option values
          onChange({
            ...filterRowSelection,
            value: allOptionValues.map(val => (val === '' ? null : val)),
          });
        } else {
          // When deselecting ALL, clear the selection
          onChange({ ...filterRowSelection, value: [] });
        }
        return newShowAll;
      });
    } else {
      setShowAll(false);
      // Convert '' back to null for "Unspecified"
      onChange({
        ...filterRowSelection,
        value: vals.map(v => (v === '' ? null : v)),
      });
    }
  };

  const valueForSelect = showAll ? allOptionValues : normalizedSelected;

  // Helper function to get friendly name for an option
  const getFriendlyName = (value: string) => {
    if (filterDefinition.isArrayField && flagMapping[value]) {
      return flagMapping[value].friendlyName;
    }
    return getLabelFromOptionValues(
      labelMapping,
      [value === '' ? null : value],
      flagMapping
    );
  };

  const renderOptions = () => {
    // The one true "Unspecified" is the entry whose .value is null/undefined:
    const unspecifiedOption = safeOptions.find(opt => opt.value == null);

    // Everything else with a non-null, non-empty-string value (and not "ALL"):
    const otherOptions = safeOptions.filter(
      opt => opt.value != null && opt.value !== ''
    );

    // Build "ALL" + "Unspecified (if present and not array field)" + genuine rest:
    const orderedList: Array<{
      value: string;
      label: string;
      count?: number;
    }> = [];
    orderedList.push({ value: 'ALL', label: 'ALL', count: 0 });

    // Only add unspecified option if it exists and this is not an array field
    if (unspecifiedOption && !filterDefinition.isArrayField) {
      orderedList.push({
        value: '',
        label: getFriendlyName(''),
        count: unspecifiedOption.count,
      });
    }

    otherOptions.forEach(opt => {
      const normalizedValue = normalize(opt.value);
      orderedList.push({
        value: normalizedValue,
        label: getFriendlyName(normalizedValue),
        count: opt.count,
      });
    });

    return orderedList.map(opt => {
      const val = opt.value === 'ALL' ? 'ALL' : opt.value;
      const checked =
        val === 'ALL' ? showAll : showAll || normalizedSelected.includes(val);

      // Only show count for database fields when EQUAL operator is selected
      const isEqualOperator =
        filterRowSelection.relationalOperator === RelationalOperator.EQUAL ||
        !filterRowSelection.relationalOperator; // Default to EQUAL if no operator set
      const showCount = isDatabaseField && isEqualOperator;
      const showFlagTotal = filterDefinition.isArrayField;

      const text =
        val === 'ALL'
          ? 'ALL'
          : showCount
            ? showFlagTotal
              ? `${opt.label} (${opt.count} Flags)`
              : `${opt.label} (${opt.count})`
            : `${opt.label}`;

      return (
        <MenuItem key={val === '' ? 'UNSPECIFIED' : val} value={val}>
          <Checkbox checked={checked} />
          <ListItemText primary={text} />
        </MenuItem>
      );
    });
  };

  return (
    <Select<string[]>
      autoFocus={autoFocus}
      id={`${label?.toLowerCase().replace(/\s+/g, '-')}-select`}
      variant="outlined"
      multiple
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      value={valueForSelect}
      onChange={handleChange}
      onFocus={onFocusChange}
      onBlur={onFocusChange}
      displayEmpty
      renderValue={selected => {
        const allSelected =
          selected.length > 0 &&
          allOptionValues.length > 0 &&
          selected.length === allOptionValues.length &&
          allOptionValues.every(val => selected.includes(val));

        if (showAll || allSelected) return 'ALL';
        return selected.length
          ? getLabelFromOptionValues(labelMapping, selected, flagMapping)
          : '';
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: 35,
          bgcolor: theme => theme.palette.grey.A200,
        }}
      >
        <IconButton
          size="small"
          aria-label="close"
          onClick={e => {
            e.stopPropagation();
            setOpen(false);
          }}
          sx={{
            position: 'absolute',
            right: 3,
            top: 3,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
        {/* Show a lightweight inline loading indicator while fetching, without replacing options */}
        {isLoading && open && (
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              top: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: theme => theme.palette.grey[600],
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption">Updating…</Typography>
          </Box>
        )}
      </Box>

      {/* Keep options rendered while open to avoid flicker/close; only show placeholder when closed */}
      {isLoading && !open ? (
        <MenuItem disabled>
          <CircularProgress size={20} />
          <Typography ml={2}>Loading...</Typography>
        </MenuItem>
      ) : (
        renderOptions()
      )}
    </Select>
  );
};

export default FilterRowMultiSelect;
